/**
 * MarketTrendPage — Real-time technical analysis for ANY token.
 *
 * Decoupled from the bot system. Now driven by a symbol selector that
 * fetches real OHLCV from Binance and runs the same indicator engine
 * (MACD, RSI, Bollinger, EMAs, SMAs, Volume, VWAP, ATR) plus a fused
 * trend score. No bot configuration required.
 *
 * Default symbol: BTC/USDT. Switchable to any major pair.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Minus, RefreshCw, Activity, BarChart2, Eye,
  Search, Zap,
} from 'lucide-react';
import { Card, CardHeader, Spinner, Badge, InfoTip } from '../components/ui';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar, ReferenceLine,
} from 'recharts';
import * as api from '../services/api';

// Popular pairs — quick-pick. Custom symbol entry also supported.
const POPULAR_PAIRS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT', 'DOGE/USDT',
  'ADA/USDT', 'AVAX/USDT', 'LINK/USDT', 'DOT/USDT', 'MATIC/USDT', 'ARB/USDT',
];

function TrendDirectionBar({ direction, strength, confidence }) {
  const color = direction === 'Bullish' ? 'bg-emerald-500' : direction === 'Bearish' ? 'bg-red-500' : 'bg-amber-400';
  const textColor = direction === 'Bullish' ? 'text-emerald-400' : direction === 'Bearish' ? 'text-red-400' : 'text-amber-400';
  const Icon = direction === 'Bullish' ? TrendingUp : direction === 'Bearish' ? TrendingDown : Minus;
  return (
    <Card>
      <div className="flex items-center gap-4 mb-3">
        <Icon className={`w-8 h-8 ${textColor}`} />
        <div>
          <p className="text-gray-400 text-sm flex items-center gap-1.5">
            Trend Direction
            <InfoTip side="right">Fused consensus across 8 indicators (RSI, MACD, Bollinger, EMAs, SMAs, Volume, VWAP, ATR). Bullish / Bearish / Neutral verdict.</InfoTip>
          </p>
          <p className={`text-2xl font-bold ${textColor}`}>{direction}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-gray-400 text-xs flex items-center justify-end gap-1.5">
            Confidence
            <InfoTip side="left">How unanimous the 8 indicators are. High confidence = most agree. Low confidence (&lt;60%) = indicators are split, no clear edge.</InfoTip>
          </p>
          <p className="text-white font-bold">{confidence}%</p>
        </div>
      </div>
      <div className="relative h-4 bg-dark-600 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${strength}%` }} />
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>Weak</span>
        <span className="inline-flex items-center gap-1.5">
          Momentum: {strength}%
          <InfoTip side="top">How decisively price is moving. &lt;40 = weak, 40-70 = moderate, 70+ = decisive. Different from Confidence (which measures indicator agreement).</InfoTip>
        </span>
        <span>Strong</span>
      </div>
    </Card>
  );
}

function IndicatorCard({ title, children, signal, icon: Icon }) {
  const signalColor = signal === 'bullish' || signal === 'buy' || signal === 'oversold'
    ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5'
    : signal === 'bearish' || signal === 'sell' || signal === 'overbought'
    ? 'text-red-400 border-red-500/20 bg-red-500/5'
    : 'text-amber-400 border-amber-500/20 bg-amber-500/5';

  return (
    <Card className={`border ${signalColor}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${signalColor.split(' ')[0]}`} />}
        <h4 className="font-semibold text-white text-sm">{title}</h4>
        <span className={`ml-auto text-xs font-medium uppercase ${signalColor.split(' ')[0]}`}>
          {signal || 'neutral'}
        </span>
      </div>
      {children}
    </Card>
  );
}

export default function MarketTrendPage() {
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [customSymbol, setCustomSymbol] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const res = await api.getMarketTrend(symbol);
      setData(res?.data || res);
    } catch (err) {
      setData({ dataSource: 'unavailable', reason: err.message });
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [symbol]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchData, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchData]);

  function applyCustomSymbol() {
    const s = customSymbol.trim().toUpperCase();
    if (!s) return;
    setSymbol(s.includes('/') ? s : s + '/USDT');
    setCustomSymbol('');
  }

  const d = data || {};
  const ind = d.indicators || {};
  const trend = d.trend || ind.trend || {};

  const trendChartData = (d.trendHistory || []).map((t, i) => ({
    hour: `-${23 - i}h`,
    strength: t.strength,
    confidence: t.confidence,
    label: t.direction,
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Market Trend Detection</h1>
          <p className="text-gray-400 text-sm mt-1">
            Real-time technical analysis across MACD, RSI, Bollinger, EMAs, SMAs, VWAP, ATR and volume — for any major pair.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select value={symbol} onChange={e => setSymbol(e.target.value)} className="input w-auto text-sm">
            {POPULAR_PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
            {!POPULAR_PAIRS.includes(symbol) && <option value={symbol}>{symbol}</option>}
          </select>
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`btn-ghost text-sm flex items-center gap-2 ${autoRefresh ? 'text-accent-primary' : ''}`}
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </button>
          <button onClick={fetchData} className="btn-ghost text-sm" title="Refresh now">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Custom symbol entry */}
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={customSymbol}
            onChange={e => setCustomSymbol(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyCustomSymbol()}
            placeholder="Custom symbol — e.g. ATOM/USDT, ENS/USDT"
            className="w-full bg-dark-800 border border-dark-700 rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
          />
        </div>
        <button onClick={applyCustomSymbol} disabled={!customSymbol.trim()}
          className="px-3 py-2 rounded-xl text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-30">
          Load
        </button>
      </div>

      {/* Unavailable state */}
      {!loading && d.dataSource === 'unavailable' && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6 text-center">
          <Activity className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="text-amber-300 font-semibold mb-1">Live data unavailable for {symbol}</h3>
          <p className="text-amber-200/70 text-sm">{d.reason || 'Try a major pair like BTC/USDT, ETH/USDT, SOL/USDT.'}</p>
        </div>
      )}

      {/* Loading */}
      {loading && !data && <div className="flex items-center justify-center h-64"><Spinner size="lg" className="text-accent-primary" /></div>}

      {/* Real data */}
      {!loading && d.dataSource === 'real' && (
        <>
          <TrendDirectionBar
            direction={trend.direction || 'Neutral'}
            strength={trend.strength || 0}
            confidence={trend.confidence || 0}
          />

          {/* Indicator grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <IndicatorCard title="MACD" signal={ind.macd?.signal} icon={Zap}>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>MACD</span><span className="font-mono text-white">{ind.macd?.macd?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>Signal</span><span className="font-mono text-white">{ind.macd?.signalLine?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>Histogram</span><span className="font-mono text-white">{ind.macd?.histogram?.toFixed(4) || '—'}</span></div>
              </div>
            </IndicatorCard>

            <IndicatorCard title={`RSI (14)`} signal={ind.rsi?.signal} icon={BarChart2}>
              <div className="text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500">Value</span>
                  <span className="font-mono text-white text-base font-bold">{ind.rsi?.value?.toFixed(1) || '—'}</span>
                </div>
                <div className="relative h-2 bg-dark-600 rounded-full">
                  <div className="absolute h-full bg-gradient-to-r from-emerald-500 via-amber-400 to-red-500 rounded-full" style={{ width: '100%' }} />
                  <div className="absolute h-3 -top-0.5 w-0.5 bg-white" style={{ left: `${ind.rsi?.value || 50}%` }} />
                </div>
                <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                  <span>Oversold (30)</span><span>Neutral</span><span>Overbought (70)</span>
                </div>
              </div>
            </IndicatorCard>

            <IndicatorCard title="Bollinger Bands" signal={ind.bollinger?.signal} icon={Eye}>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>Upper</span><span className="font-mono text-white">{ind.bollinger?.upper?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>Middle</span><span className="font-mono text-white">{ind.bollinger?.middle?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>Lower</span><span className="font-mono text-white">{ind.bollinger?.lower?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between pt-1"><span>Width</span><span className="font-mono text-white">{((ind.bollinger?.width || 0) * 100).toFixed(2)}%</span></div>
              </div>
            </IndicatorCard>

            <IndicatorCard title="EMA Crossovers" signal={ind.emas?.signal} icon={TrendingUp}>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>EMA 9</span><span className="font-mono text-white">{ind.emas?.ema9?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>EMA 20</span><span className="font-mono text-white">{ind.emas?.ema20?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>EMA 50</span><span className="font-mono text-white">{ind.emas?.ema50?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>EMA 100</span><span className="font-mono text-white">{ind.emas?.ema100?.toFixed(4) || '—'}</span></div>
              </div>
            </IndicatorCard>

            <IndicatorCard title="Volume Analysis" signal={ind.volume?.signal} icon={BarChart2}>
              <div className="text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-emerald-400">Buy {((ind.volume?.buyRatio || 0.5) * 100).toFixed(0)}%</span>
                  <span className="text-red-400">{((1 - (ind.volume?.buyRatio || 0.5)) * 100).toFixed(0)}% Sell</span>
                </div>
                <div className="relative h-2 bg-red-500/30 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${(ind.volume?.buyRatio || 0.5) * 100}%` }} />
                </div>
                <div className="flex justify-between mt-1.5 text-gray-500 text-[10px]">
                  <span>Volume Ratio</span><span className="font-mono text-white">{ind.volume?.ratio?.toFixed(2) || '—'}× avg</span>
                </div>
              </div>
            </IndicatorCard>

            <IndicatorCard title="VWAP" signal={ind.vwap?.signal} icon={TrendingUp}>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>VWAP</span><span className="font-mono text-white">{ind.vwap?.value?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>Deviation</span><span className="font-mono text-white">{(ind.vwap?.deviation || 0).toFixed(2)}%</span></div>
                <div className="flex justify-between pt-1"><span>Price vs VWAP</span><span className="font-mono text-white">{(ind.vwap?.priceVsVwap || 0) > 0 ? 'Above ↑' : 'Below ↓'}</span></div>
              </div>
              <p className="text-[10px] text-gray-600 mt-2 italic">Institutional fair value reference</p>
            </IndicatorCard>

            <IndicatorCard title="ATR (Volatility)" signal={ind.atr?.signal} icon={Activity}>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>ATR Value</span><span className="font-mono text-white">{ind.atr?.value?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>ATR %</span><span className="font-mono text-white">{ind.atr?.atrPercent?.toFixed(2) || '—'}%</span></div>
                <div className="flex justify-between"><span>Volatility</span><span className="font-mono text-white uppercase">{ind.atr?.volatility || '—'}</span></div>
              </div>
            </IndicatorCard>

            <IndicatorCard title="SMA Crossovers" signal={ind.smas?.signal} icon={TrendingUp}>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>SMA 50</span><span className="font-mono text-white">{ind.smas?.sma50?.toFixed(4) || '—'}</span></div>
                <div className="flex justify-between"><span>SMA 200</span><span className="font-mono text-white">{ind.smas?.sma200?.toFixed(4) || '—'}</span></div>
              </div>
              <p className="text-[10px] text-gray-600 mt-2 italic">Long-term trend structure</p>
            </IndicatorCard>
          </div>

          {/* 24h trend timeline */}
          {trendChartData.length > 0 && (
            <Card>
              <CardHeader title="Trend Timeline — Last 24 hours" subtitle="Momentum strength + confidence per hour" />
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trendChartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="strengthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5}/>
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="hour" stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6b7280" fontSize={10} tickLine={false} axisLine={false} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                  />
                  <ReferenceLine y={50} stroke="#374151" strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="strength" stroke="#f59e0b" strokeWidth={2} fill="url(#strengthGrad)" name="Strength" />
                  <Line type="monotone" dataKey="confidence" stroke="#06b6d4" strokeWidth={1.5} dot={false} name="Confidence" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          {lastUpdated && (
            <p className="text-[11px] text-gray-600 text-center">
              Last updated {lastUpdated.toLocaleTimeString()} · Data: real OHLCV from {d.exchange || 'binance'}
            </p>
          )}
        </>
      )}
    </div>
  );
}
